<template>
    <div class="flex flex-col gap-4">
        <!-- Row 1: Sets Mode - Toggle + Sets Incrementer -->
        <template v-if="mode === 'sets'">
            <div class="grid grid-cols-2 gap-4">
                <!-- Col1 Row1: DualSelect Toggle -->
                <div>
                    <DualSelect
                        v-model="mode"
                        :options="[
                            { value: 'sets', label: $t('match_settings.sets') },
                            { value: 'legs', label: $t('match_settings.legs') },
                        ]"
                    />
                </div>

                <!-- Col2 Row1: Sets Incrementer (no title) -->
                <div class="flex items-end justify-end">
                    <integer-incrementer
                        v-model="setsValue"
                        :min="setsMin"
                        :max="setsMax"
                        :only-odd="winType === 'best_of' && mode === 'sets'"
                    />
                </div>
            </div>

            <!-- Row 2: Legs Title + Legs Incrementer -->
            <div class="grid grid-cols-2 gap-4">
                <!-- Col1 Row2: Legs Title -->
                <div class="flex items-end">
                    <h4 class="text-md font-bold uppercase">{{ $t('match_settings.legs') }}</h4>
                </div>

                <!-- Col2 Row2: Legs Incrementer -->
                <div class="flex items-end justify-end">
                    <integer-incrementer
                        v-model="legsValue"
                        :min="legsMin"
                        :max="legsMax"
                        :only-odd="winType === 'best_of'"
                    />
                </div>
            </div>
        </template>

        <!-- Row 1: Legs Mode - Toggle + Legs Incrementer only -->
        <template v-if="mode === 'legs'">
            <div class="grid grid-cols-2 gap-4">
                <!-- Col1 Row1: DualSelect Toggle -->
                <div>
                    <DualSelect
                        v-model="mode"
                        :options="[
                            { value: 'sets', label: $t('match_settings.sets') },
                            { value: 'legs', label: $t('match_settings.legs') },
                        ]"
                    />
                </div>

                <!-- Col2 Row1: Legs Incrementer (no title) -->
                <div class="flex items-end justify-end">
                    <integer-incrementer
                        v-model="legsValue"
                        :min="legsMin"
                        :max="legsMax"
                        :only-odd="winType === 'best_of'"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import IntegerIncrementer from './IntegerIncrementer.vue'
import DualSelect from './DualSelect.vue'

export default {
    name: 'SetsLegsSelector',
    components: {
        IntegerIncrementer,
        DualSelect,
    },
    props: {
        modelValue: {
            type: Object,
            required: true,
            // Expected: { sets_to_win: number, legs_to_win: number }
        },
        setsMin: {
            type: Number,
            default: 1,
        },
        setsMax: {
            type: Number,
            default: 9,
        },
        legsMin: {
            type: Number,
            default: 1,
        },
        legsMax: {
            type: Number,
            default: 9,
        },
        initialMode: {
            type: String,
            default: 'sets',
            validator: (value) => ['sets', 'legs'].includes(value),
        },
        winType: {
            type: String,
            default: 'first_to',
            validator: (value) => ['best_of', 'first_to'].includes(value),
        },
    },
    data() {
        return {
            mode: this.initialMode,
        }
    },
    computed: {
        setsValue: {
            get() {
                return this.modelValue.sets_to_win
            },
            set(value) {
                let adjustedValue = value
                if (this.winType === 'best_of' && this.mode === 'sets') {
                    adjustedValue = this.ensureOdd(value, this.setsMin, this.setsMax)
                }
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    sets_to_win: adjustedValue,
                })
            },
        },
        legsValue: {
            get() {
                return this.modelValue.legs_to_win
            },
            set(value) {
                let adjustedValue = value
                if (this.winType === 'best_of') {
                    adjustedValue = this.ensureOdd(value, this.legsMin, this.legsMax)
                }
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    legs_to_win: adjustedValue,
                })
            },
        },
    },
    mounted() {
        if (this.winType === 'best_of') {
            if (this.mode === 'sets') {
                const currentSets = this.modelValue.sets_to_win
                const currentLegs = this.modelValue.legs_to_win
                if (currentSets % 2 === 0) {
                    this.setsValue = this.ensureOdd(currentSets, this.setsMin, this.setsMax)
                }
                if (currentLegs % 2 === 0) {
                    this.legsValue = this.ensureOdd(currentLegs, this.legsMin, this.legsMax)
                }
            } else {
                const currentLegs = this.modelValue.legs_to_win
                if (currentLegs % 2 === 0) {
                    this.legsValue = this.ensureOdd(currentLegs, this.legsMin, this.legsMax)
                }
            }
        }
    },
    watch: {
        mode() {
            if (this.mode === 'sets' || this.mode === 'legs') {
                this.$emit('mode-changed', this.mode)
                this.setsValue = 1
            }
            if (this.winType === 'best_of') {
                if (this.mode === 'sets') {
                    const currentSets = this.modelValue.sets_to_win
                    const currentLegs = this.modelValue.legs_to_win
                    if (currentSets % 2 === 0) {
                        this.setsValue = this.ensureOdd(currentSets, this.setsMin, this.setsMax)
                    }
                    if (currentLegs % 2 === 0) {
                        this.legsValue = this.ensureOdd(currentLegs, this.legsMin, this.legsMax)
                    }
                } else {
                    const currentLegs = this.modelValue.legs_to_win
                    if (currentLegs % 2 === 0) {
                        this.legsValue = this.ensureOdd(currentLegs, this.legsMin, this.legsMax)
                    }
                }
            }
        },
        'modelValue.sets_to_win'(newValue) {
            if (this.winType === 'best_of' && this.mode === 'sets' && newValue % 2 === 0) {
                const adjustedValue = this.ensureOdd(newValue, this.setsMin, this.setsMax)
                if (adjustedValue !== newValue) {
                    this.$emit('update:modelValue', {
                        ...this.modelValue,
                        sets_to_win: adjustedValue,
                    })
                }
            }
        },
        'modelValue.legs_to_win'(newValue) {
            if (this.winType === 'best_of' && newValue % 2 === 0) {
                const adjustedValue = this.ensureOdd(newValue, this.legsMin, this.legsMax)
                if (adjustedValue !== newValue) {
                    this.$emit('update:modelValue', {
                        ...this.modelValue,
                        legs_to_win: adjustedValue,
                    })
                }
            }
        },
        winType(newWinType) {
            if (newWinType === 'best_of') {
                if (this.mode === 'sets') {
                    const currentSets = this.modelValue.sets_to_win
                    const currentLegs = this.modelValue.legs_to_win
                    if (currentSets % 2 === 0) {
                        const adjustedSets = this.ensureOdd(currentSets, this.setsMin, this.setsMax)
                        this.$emit('update:modelValue', {
                            ...this.modelValue,
                            sets_to_win: adjustedSets,
                        })
                    }
                    if (currentLegs % 2 === 0) {
                        const adjustedLegs = this.ensureOdd(currentLegs, this.legsMin, this.legsMax)
                        this.$emit('update:modelValue', {
                            ...this.modelValue,
                            legs_to_win: adjustedLegs,
                        })
                    }
                } else {
                    const currentLegs = this.modelValue.legs_to_win
                    if (currentLegs % 2 === 0) {
                        const adjustedLegs = this.ensureOdd(currentLegs, this.legsMin, this.legsMax)
                        this.$emit('update:modelValue', {
                            ...this.modelValue,
                            legs_to_win: adjustedLegs,
                        })
                    }
                }
            }
        },
    },
    methods: {
        ensureOdd(value, min, max) {
            if (value % 2 === 0) {
                const nextOdd = value + 1
                const prevOdd = value - 1

                if (nextOdd <= max) {
                    return nextOdd
                } else if (prevOdd >= min) {
                    return prevOdd
                } else {
                    return min % 2 === 1 ? min : min + 1
                }
            }
            return value
        },
    },
}
</script>
